"use client";

import { ArrowRight, FileText, Hourglass, ChartPie as PieChart, ShieldCheck } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PoweringYourSpace() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    eyebrow: "RESIDENTIAL",
    title: "Home Electrical Systems",
    description: "Comprehensive wiring, lighting installations, and panel upgrades designed for modern living. We ensure your home remains safe, efficient, and fully powered for every appliance and device.",
    icon: Hourglass,
    featured: false
  }, {
    eyebrow: "COMMERCIAL",
    title: "Commercial Infrastructure",
    description: "High-capacity electrical solutions for businesses. From office lighting retrofits to complex industrial power distribution, we keep your operations running without interruption.",
    icon: PieChart,
    featured: true
  }, {
    eyebrow: "EMERGENCY",
    title: "24/7 Repair Services",
    description: "Electrical issues don't wait for business hours. Our licensed technicians are on standby to resolve power outages, short circuits, and hazardous wiring faults immediately.",
    icon: ShieldCheck,
    featured: false
  }, {
    eyebrow: "SAFETY",
    title: "Compliance & Inspections",
    description: "Protect your property with professional safety audits. We verify that your electrical systems meet all local codes and standards, providing peace of mind for your family or staff.",
    icon: FileText,
    featured: false
  }];
  return <section data-section-id="800" id="powering-your-space" className="bg-background text-foreground py-20 md:py-32">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-primary" aria-hidden="true" />
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-primary">
                Services
              </span>
            </div>
            <h2 className="font-default font-bold text-foreground mt-6 leading-[1.08] tracking-tight" style={{
            fontSize: "clamp(2.25rem, 3.5vw, 3.5rem)"
          }}>
              Powering your space with precision.
            </h2>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 lg:pt-4">
            <p className="text-[17px] leading-[1.7] text-foreground/80 font-default">
              At Current Pulse Electric, we combine technical expertise with 
              unwavering safety standards. Whether it is a residential upgrade 
              or a critical commercial repair, we deliver reliable results 
              built to last.
            </p>
            <Link to="/OurExpertisePage" className="group mt-8 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.18em] text-primary hover:text-primary/80 transition-colors">
              <span className="relative">
                View all services
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
              </span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <div ref={grid.ref} className={`mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-700 ease-out delay-100 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {services.map((service, i) => {
          const Icon = service.icon;
          const isFeatured = service.featured;
          return <Card data-index={i} key={service.title} className={`group relative overflow-hidden rounded-none border p-10 transition-all duration-500 ease-out hover:-translate-y-1 ${isFeatured ? "bg-secondary text-secondary-foreground border-secondary hover:shadow-xl" : "bg-background border-border hover:shadow-lg hover:border-primary/40"}`}>
                {}
                <div className={`h-px transition-all duration-500 ease-out ${isFeatured ? "bg-primary" : "bg-primary"} w-6 group-hover:w-12`} aria-hidden="true" />

                {}
                <div className={`mt-4 font-mono text-[11px] uppercase tracking-[0.22em] ${isFeatured ? "text-primary" : "text-muted-foreground"}`}>
                  {service.eyebrow}
                </div>

                {}
                <div className="mt-10">
                  <Icon strokeWidth={1.25} className={`size-10 transition-colors duration-500 ${isFeatured ? "text-primary group-hover:text-primary" : "text-secondary group-hover:text-primary"}`} />
                </div>

                {}
                <h3 className={`font-default font-bold mt-8 leading-tight tracking-tight ${isFeatured ? "text-secondary-foreground" : "text-foreground"}`} style={{
              fontSize: "26px"
            }}>
                  {service.title}
                </h3>

                {}
                <p className={`mt-5 text-[15px] leading-[1.65] font-default ${isFeatured ? "text-secondary-foreground/75" : "text-muted-foreground"}`}>
                  {service.description}
                </p>

                {}
                <div className="mt-10">
                  <Link to="/LetsTalkPage" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary hover:text-primary/80">
                    <span className="relative">
                      Request Service Now
                      <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                    </span>
                    <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}